import { CurrencyAmount, Token } from '@uniswap/sdk-core';
import {
  cbethAddress,
  daiAddress,
  lsethAddress,
  usdcAddress,
  cbbtcAddress,
  eurcAddress,
  ethAddress,
  tabcAddress,
  tlmnAddress,
  txyzAddress,
  tcoinAddress,
  tspyAddress,
} from '@generated/generated';
import { toJSBI } from '@/utils/bigInt';
import { getCurrentEnvironment, Environment } from '@/onchain/store/environment';

export type AssetConfig = {
  name: string;
  displaySymbol: string;
  exponent: number;
  imageUrl: string;
  address: Record<number, string>;
  isNative?: boolean;
  /** Whether this is a tokenized equity asset */
  isEquityToken?: boolean;
  /** Coinbase Resource Name — used to match dividend payouts to assets */
  cbrn?: string;
};

const baseAssets: AssetConfig[] = [
  {
    name: 'Ethereum',
    displaySymbol: 'ETH',
    exponent: 18,
    imageUrl:
      'https://dynamic-assets.coinbase.com/dbb4b4983bde81309ddab83eb598358eb44375b930b94687ebe38bc22e52c3b2125258ffb8477a5ef22e33d6bd72e32a506c391caa13af64c00e46613c3e5806/asset_icons/4113b082d21cc5fab17fc8f2d19fb996165bcce635e6900f7fc2d57c4ef33ae9.png',
    address: ethAddress,
    isNative: true,
  },
  {
    name: 'USD Coin',
    displaySymbol: 'USDC',
    exponent: 6,
    imageUrl:
      'https://dynamic-assets.coinbase.com/3c15df5e2ac7d4abbe9499ed9335041f00c620f28e8de2f93474a9f432058742cdf4674bd43f309e69778a26969372310135be97eb183d91c492154176d455b8/asset_icons/9d67b728b6c8f457717154b3a35f9ddc702eae7e76c4684ee39302c4d7fd0bb8.png',
    address: usdcAddress,
  },
  {
    name: 'Dai',
    displaySymbol: 'DAI',
    exponent: 18,
    imageUrl:
      'https://dynamic-assets.coinbase.com/90184cca292578d533bb00d9ee98529b889c15126bb120582309286b9129df9886781b30c85c21ee9cae9f2db6dc11e88633c7361fdd1ba5046ea444e101ae15/asset_icons/ebc24b163bf1f58a9732a9a1d2faa5b2141b041d754ddc2260c5e76edfed261e.png',
    address: daiAddress,
  },
  {
    name: 'Coinbase Wrapped Staked ETH',
    displaySymbol: 'cbETH',
    exponent: 18,
    imageUrl:
      'https://dynamic-assets.coinbase.com/55517c7b84cff054d0bf84f6d82680b5368915854f855ec4fe0178478a637b18a985ec85b87387beb2ae2a1b276fcb320ac7451c358302ceebb179c58934ff65/asset_icons/93aa525880c2df46f3d5404cff1844a42f1a5d1fc812128ae3f70f2ce4a882e1.png',
    address: cbethAddress,
  },
  {
    name: 'Liquid Staked ETH',
    displaySymbol: 'LsETH',
    exponent: 18,
    imageUrl:
      'https://dynamic-assets.coinbase.com/9b9902d5f68f4c95a2528db7327202a8a4100d391b226ffd17bc8ad3ad572d82d3dec6233a09451995aef91a2cbe8a4a092851320a30c3445acf087819469812/asset_icons/6170ab4319ed8958c48803ada98044953f23cd954ead6bb56f5032166971ad05.png',
    address: lsethAddress,
  },
  {
    name: 'Coinbase Wrapped Bitcoin',
    displaySymbol: 'cbBTC',
    exponent: 8,
    imageUrl: '/cbbtc.png',
    address: cbbtcAddress,
  },
  {
    name: 'EURC',
    displaySymbol: 'EURC',
    exponent: 6,
    imageUrl:
      'https://asset-metadata-service-production.s3.amazonaws.com/asset_icons/54f4216472dd25b1ffb5caf32cc0d81f645c84be166cd713f759a80f05a1418f.png',
    address: eurcAddress,
  },
];

// tEquity tokens for development environments only
export const tEquityAssets: AssetConfig[] = [
  {
    name: 'ABC',
    displaySymbol: 'tABC',
    exponent: 6,
    imageUrl: '/tabc.png',
    address: tabcAddress,
    isEquityToken: true,
  },
  {
    name: 'LMN',
    displaySymbol: 'tLMN',
    exponent: 6,
    imageUrl: '/tlmn.jpg',
    address: tlmnAddress,
    isEquityToken: true,
  },
  {
    name: 'XYZ',
    displaySymbol: 'tXYZ',
    exponent: 6,
    imageUrl: '/txyz.jpg',
    address: txyzAddress,
    isEquityToken: true,
  },
  {
    name: 'COIN',
    displaySymbol: 'tCOIN',
    exponent: 6,
    imageUrl: '/tcoin.png',
    address: tcoinAddress,
    isEquityToken: true,
    cbrn: 'v1:equity:::08207eeab8ba9d9f78bb2d53c39839f1c6e1ae9e53cab5f299023b0ead86a939:',
  },
  {
    name: 'SPY',
    displaySymbol: 'tSPY',
    exponent: 6,
    imageUrl: '/tspy.png',
    address: tspyAddress,
    isEquityToken: true,
    cbrn: 'v1:equity:::tspy-placeholder:',
  },
];

// Derived list of tEquity token symbols for use across the app
export const TEQUITY_SYMBOLS = tEquityAssets.map((a) => a.displaySymbol);

// Create immutable ASSETS array based on environment
const currentEnv = getCurrentEnvironment();
const isDevEnvironment =
  currentEnv === Environment.development || currentEnv === Environment.localhost;

export const ASSETS: AssetConfig[] = [...baseAssets, ...(isDevEnvironment ? tEquityAssets : [])];

export const assetBySymbol = (symbol: string | undefined): AssetConfig | undefined => {
  return ASSETS.find((asset) => asset.displaySymbol.toLowerCase() === symbol?.toLowerCase());
};

export const assetByCbrn = (cbrn: string | undefined): AssetConfig | undefined => {
  if (!cbrn) return undefined;
  return ASSETS.find((asset) => asset.cbrn?.toLowerCase() === cbrn.toLowerCase());
};

export const assetByAddress = (
  chainId: number,
  address: string | undefined,
): AssetConfig | undefined => {
  // Return undefined early if no address is provided
  if (!address) {
    return undefined;
  }
  return ASSETS.find((asset) => asset.address[chainId]?.toLowerCase() === address?.toLowerCase());
};

export const parseAssetAmountInMinUnit = (
  amountInMinUnit: bigint | undefined,
  asset: AssetConfig | undefined,
): number => {
  if (!asset || !amountInMinUnit) return 0;
  // default decimal places = 8
  const decimalPlaces = 8;
  if (decimalPlaces > asset.exponent) {
    return (
      Number(amountInMinUnit * BigInt(10 ** (decimalPlaces - asset.exponent))) /
      Number(10 ** decimalPlaces)
    );
  } else {
    return (
      Number(amountInMinUnit / BigInt(10 ** (asset.exponent - decimalPlaces))) /
      Number(10 ** decimalPlaces)
    );
  }
};

export const getZeroForOneForSwap = (chainId: number, input: string, output: string): boolean => {
  let zeroForOne = true;
  const inputAsset = assetBySymbol(input);
  const outputAsset = assetBySymbol(output);

  if (!inputAsset || !outputAsset) {
    throw new Error(`Asset not found for symbols: ${input}, ${output}`);
  }

  if (inputAsset.address[chainId] > outputAsset.address[chainId]) {
    zeroForOne = false;
  }
  return zeroForOne;
};

export const getCurrencyAmount = ({
  chainId,
  // raw amount in min unit
  rawAmount,
  asset,
}: {
  chainId: number;
  rawAmount: bigint;
  asset: AssetConfig | undefined;
}): CurrencyAmount<Token> | undefined => {
  if (!asset) return undefined;
  const token = new Token(
    chainId,
    asset.address[chainId],
    asset.exponent,
    asset.displaySymbol,
    asset.imageUrl,
  );
  return CurrencyAmount.fromRawAmount(token, toJSBI(rawAmount));
};

export const isNativeToken = (chainId: number, tokenAddress: string): boolean => {
  return assetByAddress(chainId, tokenAddress)?.isNative ?? false;
};
